import type { ResolvedToolDefinition, ToolBenefit } from "@/data/tools";
import ToolSectionHeader from "./ToolSectionHeader";
import ToolIcon from "./ToolIcon";

type ToolBenefitsSectionProps = {
  tool: ResolvedToolDefinition;
  benefits?: ToolBenefit[];
};

export default function ToolBenefitsSection({
  tool,
  benefits,
}: ToolBenefitsSectionProps) {
  const section = tool.pageContent?.sections?.benefits;
  const resolvedBenefits = benefits ?? tool.pageContent?.benefits ?? [];
  const hasSectionCopy = Boolean(section?.title || section?.description);

  if (!resolvedBenefits.length && !hasSectionCopy) {
    return null;
  }

  return (
    <section
      className="max-w-4xl mx-auto space-y-8"
      aria-labelledby="benefits-heading"
    >
      <ToolSectionHeader
        headingId="benefits-heading"
        title={section?.title ?? "Why Use This Tool?"}
        description={section?.description}
      />
      {resolvedBenefits.length ? (
        <BenefitsGrid benefits={resolvedBenefits} />
      ) : (
        <div className="rounded-2xl border border-dashed border-[#d7cbf6] bg-gradient-to-br from-white to-[#faf7ff] p-6 text-center text-sm text-gray-500">
          No benefit items are configured for this tool yet, but the section write-up is still visible.
        </div>
      )}
    </section>
  );
}

type BenefitsGridProps = {
  benefits: readonly ToolBenefit[];
};

function BenefitsGrid({ benefits }: BenefitsGridProps) {
  const iconColorClasses = [
    "text-[#c3003a]",
    "text-[#5b32b4]",
    "text-[#1f6feb]",
  ] as const;

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {benefits.map((benefit, index) => (
        <div
          key={benefit.title}
          className={`border rounded-2xl p-5 hover:border-[#5b32b4]/40 hover:shadow-md hover:shadow-[#5b32b4]/10 hover:-translate-y-0.5 transition-all duration-300 group ${
            index % 3 === 0
              ? "bg-gradient-to-br from-white to-[#faf7ff] border-[#e9e1ff]"
              : index % 3 === 1
                ? "bg-gradient-to-br from-white to-[#fff7fb] border-[#f4d7e6]"
                : "bg-gradient-to-br from-white to-[#f7fbff] border-[#d9e7ff]"
          }`}
        >
          <div className="w-10 h-10 rounded-xl bg-white/80 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-sm">
            <ToolIcon
              icon={benefit.icon}
              size={20}
              className={iconColorClasses[index % iconColorClasses.length]}
            />
          </div>
          <h3 className="font-bold text-[#2b1d3a] mb-1.5 text-sm">
            {benefit.title}
          </h3>
          <p className="text-gray-500 text-sm leading-relaxed">
            {benefit.description}
          </p>
        </div>
      ))}
    </div>
  );
}
